import isNil from 'lodash/isNil';
import {
  EventTimelineItem,
} from 'core/components/Charts/EventTimeline';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  RTMLastCheckInSummaryDTO,
  RTMPatientTrendResponseDTO,
  RTMReportedBarrierDTO,
  RTMTrendPointDTO,
} from 'dtos';
import {
  RTMMonitoringPriority,
} from 'dtos/rTMMonitoringPriority';
import {
  MetricTone,
  SeriesTrendDirection,
  SeriesTrendLabel,
} from 'pages/Dashboard/pages/Dashboard/RTM/Trends/constants';
import {
  formatDate,
} from 'utils/date';
import dayjs from 'utils/dayjs';

export type TrendChartRow = {
  completedOn: string;
  peg3Composite?: number;
  averagePain?: number;
  painInterferenceEnjoyment?: number;
  painInterferenceActivity?: number;
  conditionChangeRank?: number;
  carePlanAdherenceRank?: number;
};

export type TrendMetadata = {
  lastCheckinDate?: string | null;
  reportsInPeriod: number;
  dateRangeLabel: string;
};

export type PatientTrendSummaryViewModel = {
  lastCheckIn?: RTMLastCheckInSummaryDTO;
  barriers: RTMReportedBarrierDTO[];
  metadata: TrendMetadata;
  suddenWorseningValue: string;
  suddenWorseningDescription: string;
  suddenWorseningTone: MetricTone;
  conditionLabel?: string;
  adherenceLabel?: string;
  conditionDescription?: string;
  adherenceDescription?: string;
};

const TREND_NUMERIC_KEYS = [
  'peg3Composite',
  'averagePain',
  'painInterferenceEnjoyment',
  'painInterferenceActivity',
  'conditionChangeRank',
  'carePlanAdherenceRank',
] as const satisfies ReadonlyArray<keyof Omit<TrendChartRow, 'completedOn'>>;

export function getTrendChartRows(
  points?: RTMTrendPointDTO[] | null,
): TrendChartRow[] {
  return [...(points ?? [])]
    .sort((left, right) => (
      dayjs(left.completedOn).valueOf() - dayjs(right.completedOn).valueOf()
    ))
    .map((point) => {
      const row: TrendChartRow = {
        completedOn: formatDate(point.completedOn ?? ''),
      };

      TREND_NUMERIC_KEYS.forEach((key) => {
        const value = point[key];
        if (!isNil(value)) {
          row[key] = value;
        }
      });

      return row;
    });
}

export function getEventTimelineItems(
  points?: RTMTrendPointDTO[] | null,
): EventTimelineItem[] {
  return [...(points ?? [])]
    .filter((point) => !isNil(point.completedOn))
    .sort((left, right) => (
      dayjs(left.completedOn).valueOf() - dayjs(right.completedOn).valueOf()
    ))
    .map((point) => ({
      date: formatDate(point.completedOn ?? ''),
      hasSuddenWorsening: point.suddenWorsening === true,
    }));
}

function getSuddenWorseningValue(count?: number | null): string {
  return isNil(count) ? DEFAULT_EMPTY_VALUE : String(count);
}

function getSuddenWorseningDescription(count?: number | null): string {
  return !isNil(count) && count > 0
    ? 'check-ins in a row'
    : 'No consecutive worsening';
}

function getSuddenWorseningTone(count?: number | null): MetricTone {
  return !isNil(count) && count > 0 ? 'danger' : 'default';
}

export function getPainTone(peg3?: number | null): MetricTone {
  if (isNil(peg3)) {
    return 'default';
  }

  if (peg3 <= 3) {
    return 'info';
  }

  return peg3 <= 6 ? 'warning' : 'danger';
}

export function getConditionTone(rank?: number | null): MetricTone {
  if (isNil(rank)) {
    return 'default';
  }

  if (rank <= 2) {
    return 'danger';
  }

  if (rank === 3) {
    return 'warning';
  }

  return 'info';
}

export function getAdherenceTone(rank?: number | null): MetricTone {
  if (isNil(rank)) {
    return 'default';
  }

  if (rank <= 2) {
    return 'danger';
  }

  return rank === 3 ? 'warning' : 'info';
}

export function getSeriesTrendLabel(
  current?: number | null,
  prior?: number | null,
  direction: SeriesTrendDirection = 'higherBetter',
): SeriesTrendLabel {
  if (isNil(current) || isNil(prior) || current === prior) {
    return 'No change';
  }

  const improved = direction === 'lowerBetter'
    ? current < prior
    : current > prior;

  return improved ? 'Improving' : 'Worsening';
}

export function getLastTwoNumericValues(
  rows: TrendChartRow[],
  key: keyof Omit<TrendChartRow, 'completedOn'>,
): {
  current?: number;
  prior?: number;
} {
  const values = rows
    .map((row) => row[key])
    .filter((value): value is number => !isNil(value));

  return values.length === 0
    ? {}
    : {
      current: values[values.length - 1],
      prior: values.length > 1 ? values[values.length - 2] : undefined,
    };
}

export function getTrendMetadata(
  patientTrend?: RTMPatientTrendResponseDTO,
): TrendMetadata {
  const points = [...(patientTrend?.points ?? [])]
    .filter((point) => !isNil(point.completedOn))
    .sort((left, right) => (
      dayjs(left.completedOn).valueOf() - dayjs(right.completedOn).valueOf()
    ));
  const lastCheckInOn = patientTrend?.lastCheckIn?.completedOn
    ?? points[points.length - 1]?.completedOn;
  const firstPointOn = points[0]?.completedOn;
  const lastPointOn = points[points.length - 1]?.completedOn;
  const dateRangeLabel = !isNil(firstPointOn) && !isNil(lastPointOn)
    ? `${dayjs(firstPointOn).format('MMM D')} – ${dayjs(lastPointOn).format('MMM D, YYYY')}`
    : DEFAULT_EMPTY_VALUE;

  return {
    lastCheckinDate: lastCheckInOn,
    reportsInPeriod: points.length,
    dateRangeLabel,
  };
}

export function getPriorityTone(priority?: RTMMonitoringPriority): MetricTone {
  if (priority === RTMMonitoringPriority.NeedsReview) {
    return 'danger';
  }

  if (priority === RTMMonitoringPriority.Watch) {
    return 'warning';
  }

  if (priority === RTMMonitoringPriority.Stable) {
    return 'info';
  }

  return 'default';
}

function getCheckInMetricLabel(
  label?: string | null,
  rank?: number | null,
): string | undefined {
  return label ?? (isNil(rank) ? undefined : String(rank));
}

function getAdherenceDescription(rank?: number | null): string | undefined {
  if (isNil(rank)) {
    return undefined;
  }

  if (rank >= 4) {
    return 'Consistent adherence';
  }

  if (rank === 3) {
    return 'Mixed adherence';
  }

  return 'Low adherence';
}

function getConditionDescription(
  rank?: number | null,
  priorRank?: number | null,
): string | undefined {
  if (isNil(rank)) {
    return undefined;
  }

  if (rank === 4 || (!isNil(priorRank) && priorRank === rank)) {
    return 'No change from prior';
  }

  if (isNil(priorRank)) {
    return undefined;
  }

  return rank > priorRank ? 'Improved from prior' : 'Worsened from prior';
}

export function getPatientTrendSummaryViewModel(
  patientTrend: RTMPatientTrendResponseDTO | undefined,
  chartRows: TrendChartRow[],
): PatientTrendSummaryViewModel {
  const lastCheckIn = patientTrend?.lastCheckIn;
  const conditionValues = getLastTwoNumericValues(chartRows, 'conditionChangeRank');

  const consecutiveWorseningCount = lastCheckIn?.consecutiveWorseningCount;

  return {
    lastCheckIn,
    barriers: patientTrend?.reportedBarriers ?? [],
    metadata: getTrendMetadata(patientTrend),
    suddenWorseningValue: getSuddenWorseningValue(consecutiveWorseningCount),
    suddenWorseningDescription: getSuddenWorseningDescription(
      consecutiveWorseningCount,
    ),
    suddenWorseningTone: getSuddenWorseningTone(consecutiveWorseningCount),
    conditionLabel: getCheckInMetricLabel(
      lastCheckIn?.conditionChangeLabel,
      lastCheckIn?.conditionChangeRank,
    ),
    adherenceLabel: getCheckInMetricLabel(
      lastCheckIn?.carePlanAdherenceLabel,
      lastCheckIn?.carePlanAdherenceRank,
    ),
    conditionDescription: getConditionDescription(
      lastCheckIn?.conditionChangeRank,
      conditionValues.prior,
    ),
    adherenceDescription: getAdherenceDescription(
      lastCheckIn?.carePlanAdherenceRank,
    ),
  };
}
